import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useTrackView } from '#src/app/shared/hooks/tracking';

import { useMembershipBottomBannerData, useMembershipBottomBannerState } from '../../../../context';
import { useBannerClosedStateStorage } from '../../../hooks/useBannerClosedStateStorage';

type Result = ModelHookResult<
	{
		visible: boolean;
		ref: RefObject<HTMLDivElement | null>;
		paymentTerm: 'one-time' | 'monthly' | 'yearly';
		showOneTimePaymentTerm: boolean;
		showYearlyPaymentTerm: boolean;
	},
	{
		onClickClose: () => void;
		onChange: (value: string) => void;
	}
>;

export function useLayout(): Result {
	const [visible, setVisible] = useState(true);
	const ref = useRef<HTMLDivElement | null>(null);
	const track = useTracking();
	useTrackView('petition_membership_popup_shown');
	const { setBannerClosed } = useBannerClosedStateStorage();
	const [{ paymentTerm }, { setPaymentTerm }] = useMembershipBottomBannerState();

	const {
		data: {
			featureConfigs: { showOneTimeContributions, yearlyContributionsAmountsConfig },
		},
	} = useMembershipBottomBannerData();

	const onClickClose = useCallback(() => {
		void track('petition_membership_popup_closed');
		setVisible(false);
		setBannerClosed('true');
	}, [track, setBannerClosed]);

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			if (ref.current && !ref.current.contains(event.target as Node)) {
				onClickClose();
			}
		};

		document.addEventListener('click', handleClick);
		return () => document.removeEventListener('click', handleClick);
	}, [ref, onClickClose]);

	const onChange = (value: string) => {
		setPaymentTerm(value as 'one-time' | 'monthly' | 'yearly');
		void track('petition_membership_popup_payment_term_selected', { payment_term: value });
	};

	return {
		data: {
			visible,
			ref,
			paymentTerm,
			showYearlyPaymentTerm: yearlyContributionsAmountsConfig?.enabled ?? false,
			showOneTimePaymentTerm: showOneTimeContributions,
		},
		actions: {
			onClickClose,
			onChange,
		},
	};
}
